import type { ReactNode } from 'react';
import { SunMark } from '../primitives/SunMark';
import {
  PawIcon,
  CarIcon,
  PinIcon,
  ChevRightIcon,
  MusicIcon,
} from '../primitives/Icon';
import { MARKET_SCHEDULE, isOutdoorSeason, scheduleLabel } from '../../utils/marketStatus';
import { Scrollable } from '../primitives/Scrollable';
import styles from './InfoView.module.css';

const WEBSITE_URL = 'https://troymarket.org';
const WEBSITE_LABEL = 'troymarket.org';
const PARKING_URL = 'https://www.troymarket.org/parking';
const DOG_POLICY_URL = 'https://www.troymarket.org/dog-policy';
const MUSIC_URL = 'https://www.troymarket.org/music';
const MONUMENT_MAPS_URL = 'https://www.google.com/maps/search/?api=1&query=Monument+Square+Troy+NY';
const ATRIUM_MAPS_URL = 'https://www.google.com/maps/search/?api=1&query=Troy+Atrium+Troy+NY';
const GARAGE_MAPS_URL = 'https://www.google.com/maps/search/?api=1&query=5th+Ave+Garage+Troy+NY';

type LocationRowProps = {
  season: string;
  venue: string;
  address: string;
  href: string;
  active: boolean;
};

function LocationRow({ season, venue, address, href, active }: LocationRowProps) {
  return (
    <a
      className={`${styles.locationRow} ${active ? styles.locationRowActive : ''}`}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open ${venue} in Maps`}
    >
      <div className={styles.locationBody}>
        <div className={styles.locationSeasonRow}>
          <span className={styles.locationSeason}>{season}</span>
          {active ? <span className={styles.nowBadge}>Now</span> : null}
        </div>
        <div className={styles.locationVenue}>{venue}</div>
        <div className={styles.locationAddress}>{address}</div>
      </div>
      <span className={styles.locationIcon} aria-hidden="true">
        <PinIcon size={16} color="var(--color-accent-default)" />
      </span>
    </a>
  );
}

type LinkRowProps = {
  icon?: ReactNode;
  label: string;
  sublabel?: string;
  href: string;
  external?: boolean;
  ariaLabel?: string;
};

function LinkRow({ icon, label, sublabel, href, external, ariaLabel }: LinkRowProps) {
  const rel = external ? 'noopener noreferrer' : undefined;
  const target = external ? '_blank' : undefined;
  return (
    <a
      className={styles.linkRow}
      href={href}
      target={target}
      rel={rel}
      aria-label={ariaLabel}
    >
      {icon ? <span className={styles.linkRowIcon}>{icon}</span> : null}
      <span className={styles.linkRowBody}>
        <span className={styles.linkRowLabel}>{label}</span>
        {sublabel ? <span className={styles.linkRowSub}>{sublabel}</span> : null}
      </span>
      <ChevRightIcon size={16} color="var(--color-text-secondary)" />
    </a>
  );
}

export function InfoView() {
  const outdoor = isOutdoorSeason();

  return (
    <Scrollable className={styles.info}>
      <div className={styles.infoInner}>
      <div className={styles.eyebrow}>About the market</div>
      <h1 className={styles.heading}>{scheduleLabel(MARKET_SCHEDULE)}</h1>

      <div className={styles.banner}>
        <SunMark size={36} color="var(--color-accent-default)" />
        <div>
          <div className={styles.bannerTitle}>Rain or shine</div>
          <p className={styles.bannerBody}>
            Every vendor is open the full market window.
          </p>
        </div>
      </div>

      <h2 className={styles.sectionTitle}>Where we are</h2>
      <div className={styles.sectionCard}>
        <LocationRow
          season="May – Oct"
          venue="Monument Square"
          address="River St, Troy, NY"
          href={MONUMENT_MAPS_URL}
          active={outdoor}
        />
        <div className={styles.divider} />
        <LocationRow
          season="Nov – Apr"
          venue="Troy Atrium"
          address="Broadway & 4th, Troy, NY"
          href={ATRIUM_MAPS_URL}
          active={!outdoor}
        />
      </div>

      <h2 className={styles.sectionTitle}>Payments</h2>
      <div className={styles.grid}>
        <div className={styles.paymentCard}>
          <div className={styles.paymentKey}>Cash</div>
          <div className={styles.paymentDesc}>Every vendor</div>
        </div>
        <div className={styles.paymentCard}>
          <div className={styles.paymentKey}>Card</div>
          <div className={styles.paymentDesc}>Most vendors</div>
        </div>
        <div className={`${styles.paymentCard} ${styles.paymentCardWide}`}>
          <div className={styles.paymentKey}>EBT · SNAP</div>
          <div className={styles.paymentDesc}>
            Swap for $1 tokens at the info tent
          </div>
        </div>
      </div>

      <h2 className={styles.sectionTitle}>Live music</h2>
      <div className={styles.sectionCard}>
        <LinkRow
          icon={<MusicIcon size={16} color="var(--color-accent-default)" />}
          label="Weekly music schedule"
          sublabel="troymarket.org/music"
          href={MUSIC_URL}
          external
        />
      </div>

      <h2 className={styles.sectionTitle}>Parking</h2>
      <div className={styles.sectionCard}>
        <p className={styles.sectionLead}>
          <strong>Free on weekends</strong> across nearby streets, lots, and garages.
        </p>
        <a
          className={styles.pickCard}
          href={GARAGE_MAPS_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open 5th Ave Garage in Maps"
        >
          <div className={styles.pickBody}>
            <div className={styles.pickEyebrow}>Pro tip</div>
            <div className={styles.pickTitle}>5th Ave Garage</div>
            <div className={styles.pickDesc}>
              Upper decks stay open even at 11 AM. Under two blocks from the market.
            </div>
          </div>
          <span className={styles.pickIcon} aria-hidden="true">
            <PinIcon size={16} color="var(--color-accent-default)" />
          </span>
        </a>
        <LinkRow
          icon={<CarIcon size={16} color="var(--color-accent-default)" />}
          label="More parking options"
          href={PARKING_URL}
          external
        />
      </div>

      <h2 className={styles.sectionTitle}>Dogs</h2>
      <div className={styles.sectionCard}>
        <div className={styles.dogHeader}>
          <PawIcon size={20} color="var(--palette-rose)" />
          <div className={styles.dogTitle}>Seasonal policy</div>
        </div>
        <div className={styles.policyRow}>
          <div className={styles.policyLabel}>May – Oct · Outdoor</div>
          <div className={styles.policyBody}>
            Leashed pets welcome. Short, non-extendable leash; at your side; clean up after them.
          </div>
        </div>
        <div className={styles.divider} />
        <div className={styles.policyRow}>
          <div className={styles.policyLabel}>Nov – Apr · Indoor</div>
          <div className={styles.policyBody}>Service animals only.</div>
        </div>
        <LinkRow
          icon={<PawIcon size={16} color="var(--color-accent-default)" />}
          label="Full dog policy"
          href={DOG_POLICY_URL}
          external
        />
      </div>

      <a
        className={styles.websiteCard}
        href={WEBSITE_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        <div className={styles.websiteBody}>
          <div className={styles.websiteEyebrow}>Official site</div>
          <div className={styles.websiteLabel}>{WEBSITE_LABEL}</div>
          <div className={styles.websiteHint}>
            Weekly vendor list, events, and the latest updates.
          </div>
        </div>
        <svg
          className={styles.websiteIcon}
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M14 4h6v6" />
          <path d="M20 4 10 14" />
          <path d="M20 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h4" />
        </svg>
      </a>

      <div className={styles.versionFooter} aria-label={`App version ${__APP_VERSION__}`}>
        v{__APP_VERSION__}
      </div>
      </div>
    </Scrollable>
  );
}
